import { useMemo, useCallback } from 'react';
import { AutocompleteInput } from '@/components';
import type { Option } from '@/components/inputs/AutocompleteInput';
import { useOriginAddressAutocomplete } from '../../hooks';

export interface OriginAddressAutocompleteInputProps {
  name: string;
  placeholder: string;
  value: string | undefined;
  onValueChange: (next: string) => void;
  suggestionsEnabled: boolean;
}

export default function OriginAddressAutocompleteInput({
  name,
  placeholder,
  value,
  onValueChange,
  suggestionsEnabled,
}: OriginAddressAutocompleteInputProps) {
  const { originAddressOptions, isOriginAddressLoading } =
    useOriginAddressAutocomplete({
      search: value,
      minLength: 3,
      enabled: suggestionsEnabled,
    });
  const selected = useMemo((): Option | null => {
    if (!(value?.trim() ?? '')) return null;
    return { label: value!, data: value! };
  }, [value]);
  const onSelect = useCallback(
    (option: Option | null) => {
      if (!option) {
        onValueChange('');
        return;
      }
      if (typeof option.data !== 'string') return;
      onValueChange(option.data);
    },
    [onValueChange]
  );

  return (
    <AutocompleteInput
      name={name}
      placeholder={placeholder}
      value={selected}
      inputValue={value ?? ''}
      onChange={onSelect}
      onInputChange={onValueChange}
      options={originAddressOptions}
      required={false}
      loading={isOriginAddressLoading}
      freeSolo
    />
  );
}
